import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { handleCancelError, refreshAbortController } from '@/apis/axios.utils';
import * as InstagramAPI from '@/apis/instagram';
import { getPredictions } from '@/apis/library';
import { useIdentityStore } from '@/stores/identity';

export const useInstagramAccountStore = defineStore(
  'instagramAccount',
  () => {
    const identityStore = useIdentityStore();

    const pending = ref({
      igUser: false,
      addAccountStatus: false,
      brandInstagramAccount: false,
      brandedContentThumbnails: {},
    });

    const initialized = ref({
      brandInstagramAccount: false,
    });

    const error = ref({
      addAccountStatus: null,
      getIgUser: null,
    });

    const abortControllers = {
      getIgUser: ref(null),
    };

    const brandInstagramAccount = ref(null);
    const addedAccount = ref(null);
    const igUser = ref({});
    const igUserPosts = ref([]);
    const igUserNextToken = ref(null);
    const brandedContentThumbnails = ref({});
    const igUserPostsPredictions = ref({});

    const igUserPostsWithPredictions = computed(() =>
      igUserPosts.value.map((post) => {
        const prediction = igUserPostsPredictions.value[post.sourceId];
        return prediction !== undefined
          ? { ...post, predictions: { engagement: prediction ?? 'processing' } }
          : post;
      }),
    );

    async function getIgUser({ handle, after, limit }) {
      pending.value.igUser = true;
      const signal = refreshAbortController(abortControllers.getIgUser);
      try {
        // If the handle were searching doesn't match the existing posts, clear them.
        if (igUserPosts.value[0]?.handle !== handle) {
          igUserPosts.value = [];
        }

        const brandId = identityStore.currentBrand?.id;
        const response = await InstagramAPI.getInstagramUsers(
          { brandId, handle, after, limit },
          { signal },
        );
        const payload = response?.data;

        if (isEmpty(igUser.value)) {
          igUser.value = payload?.user;
        }

        if (payload?.posts) {
          const posts = payload.posts.map((item) => ({ handle: igUser.value?.handle, ...item }));
          igUserPosts.value = [...igUserPosts.value, ...posts];
          igUserNextToken.value = payload.after;
        }

        return payload;
      } catch (e) {
        error.value.getIgUser = e.response;
        if (e.response?.status === 422) {
          igUserPosts.value = [];
          return [];
        }
        return handleCancelError(e);
      } finally {
        if (!signal.aborted) {
          pending.value.igUser = false;
        }
      }
    }

    async function addAccount({ handle }) {
      pending.value.addAccountStatus = true;
      error.value.addAccountStatus = null;
      try {
        const response = await InstagramAPI.addAccount({ handle });
        const payload = response?.data;
        addedAccount.value = payload;
        return payload;
      } catch (e) {
        error.value.addAccountStatus = e;
        throw e;
      } finally {
        pending.value.addAccountStatus = false;
      }
    }

    async function getBrandInstagramAccount() {
      const brandId = identityStore.currentBrand?.id;
      pending.value.brandInstagramAccount = true;
      try {
        const response = await InstagramAPI.getBrandInstagramAccount({ brandId });
        const payload = response?.data;
        brandInstagramAccount.value = payload;
        initialized.value.brandInstagramAccount = true;
        return payload;
      } catch (e) {
        if (e?.response?.status === 404) {
          brandInstagramAccount.value = null;
        } else {
          throw e;
        }
      } finally {
        pending.value.brandInstagramAccount = false;
      }
      return undefined;
    }

    function clearBrandInstagramAccount() {
      brandInstagramAccount.value = null;
    }

    function clearIgUser() {
      igUser.value = null;
      igUserPosts.value = [];
      igUserPostsPredictions.value = {};
      error.value.getIgUser = null;
      igUserNextToken.value = null;
    }

    async function fetchBrandedContentThumbnail({ brandId, handle, post }) {
      brandedContentThumbnails.value = {};
      pending.value.brandedContentThumbnails = {};

      const postId = post.id;

      if (!post.createdAt) {
        brandedContentThumbnails.value[postId] = null;
        return;
      }

      pending.value.brandedContentThumbnails[postId] = true;

      try {
        const dateFormat = 'YYYY-MM-DDTHH:mm:ss';
        const postDate = dayjs(post.createdAt);
        const mediaSince = postDate.subtract(1, 'day').format(dateFormat);
        const mediaUntil = postDate.add(2, 'day').format(dateFormat);

        const response = await InstagramAPI.getInstagramUsers({
          brandId,
          handle,
          cache: true,
          mediaSince,
          mediaUntil,
          limit: 50,
        });

        const posts = response?.data?.posts || [];

        const matchingPost = posts.find((p) => p.sourceId === post.id);

        if (matchingPost) {
          if (matchingPost.image) post.image = matchingPost.image;
          if (matchingPost.video) post.video = matchingPost.video;
        }
      } finally {
        pending.value.brandedContentThumbnails[postId] = false;
      }
    }

    function getPostThumbnailUrl(post) {
      return (
        post.video?.thumbnails?.smallSquare?.url ?? post.image?.sizes?.smallSquare?.url ?? null
      );
    }

    async function fetchIgUserPostsPredictions({ brandId, posts }) {
      const postsWithImages = posts.filter(
        (post) =>
          getPostThumbnailUrl(post) && igUserPostsPredictions.value[post.sourceId] === undefined,
      );
      if (postsWithImages.length === 0) return;

      // Initialize predictions as processing
      postsWithImages.forEach((post) => {
        igUserPostsPredictions.value[post.sourceId] = 'processing';
      });

      await Promise.all(
        postsWithImages.map(async (post) => {
          try {
            const response = await getPredictions({
              brandId,
              files: [{ id: post.sourceId, url: getPostThumbnailUrl(post) }],
            });
            const newPredictions = response?.data ?? {};
            igUserPostsPredictions.value = {
              ...igUserPostsPredictions.value,
              ...newPredictions,
            };
          } catch (e) {
            // Clear processing state on error
            delete igUserPostsPredictions.value[post.sourceId];
            throw e;
          }
        }),
      );
    }

    return {
      pending,
      initialized,
      error,
      igUser,
      igUserPosts,
      igUserPostsWithPredictions,
      igUserPostsPredictions,
      igUserNextToken,
      addedAccount,
      brandInstagramAccount,
      clearBrandInstagramAccount,
      getIgUser,
      clearIgUser,
      addAccount,
      getBrandInstagramAccount,
      fetchBrandedContentThumbnail,
      fetchIgUserPostsPredictions,
    };
  },
  { resetOnBrandChange: true },
);
